import styles from "./SocialConnectMenu.module.css";
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import * as icons from "react-icons/fa";
import { ReactComponent as XIcon } from "../../assets/media/fa-x-icon.svg";
import { toTitleCase } from "../../Hooks/utility";
import { lockBodyScroll, unlockBodyScroll } from "../../Hooks/bodyScrollLock";

function SocialConnectMenu(props) {
  const { socialConnections } = useSelector((state) => state.contentData);
  const [modalOpen, setModalOpen] = useState(false);
  const triggerRef = useRef(null);
  const sheetRef = useRef(null);
  const closeRef = useRef(null);

  const closeModal = useCallback(() => setModalOpen(false), []);
  const toggleModal = useCallback(() => setModalOpen((open) => !open), []);

  useEffect(() => {
    if (!modalOpen) return;

    const previouslyFocused = document.activeElement;
    lockBodyScroll();

    const focusId = window.requestAnimationFrame(() => {
      closeRef.current?.focus();
    });

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setModalOpen(false);
        return;
      }
      if (e.key !== "Tab" || !sheetRef.current) return;
      const focusable = sheetRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const list = Array.from(focusable).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (list.length === 0) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusId);
      window.removeEventListener("keydown", onKeyDown);
      unlockBodyScroll();
      if (triggerRef.current) triggerRef.current.focus();
      else if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [modalOpen]);

  if (!socialConnections) return null;

  const iconObj = { ...icons };
  const outputArray = [];

  for (const value of Object.values(socialConnections)) {
    const name = "Fa" + toTitleCase(value.slug);
    if (
      value.hasOwnProperty("active") &&
      (!value.active ||
        value.active.replace(" ", "") === "" ||
        value.active.replace(" ", "") === "false")
    )
      continue;

    const href = value.hasOwnProperty("link") ? value.link : "#";
    const title = value.title;

    if (iconObj.hasOwnProperty(name)) {
      const Module = iconObj[name];
      outputArray.push(
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={props.linksStyles}
        >
          <Module aria-hidden="true" />
          <span className={styles["social-title"]}>{title}</span>
        </a>,
      );
    } else if (name === "FaX") {
      outputArray.push(
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={props.linksStyles}
        >
          <XIcon aria-hidden="true" />
          <span className={styles["social-title"]}>{title}</span>
        </a>,
      );
    } else if (value.iconURL) {
      const iconURLValue = value.iconURL.replaceAll(" ", "");

      if (iconURLValue.includes("letter-")) {
        const letter = iconURLValue.split("-");
        outputArray.push(
          <a
            key={name}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={props.linksStyles}
          >
            <span
              className={styles["letter-" + letter[1]]}
              style={{
                fontFamily:
                  letter[3] && toTitleCase(letter[3].replaceAll("_", " ")),
              }}
              aria-hidden="true"
            >
              {letter[2]}
            </span>
            <span className={styles["social-title"]}>{title}</span>
          </a>,
        );
      } else {
        outputArray.push(
          <a
            key={name}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={props.linksStyles}
          >
            <object data={value.iconURL} aria-hidden="true" />
            <span className={styles["social-title"]}>{title}</span>
          </a>,
        );
      }
    } else {
      outputArray.push(
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={props.linksStyles}
        >
          <span className={styles["social-title"]}>{title}</span>
        </a>,
      );
    }
  }

  /* Inline expanded list (e.g. mobile sheet menu); no popup trigger */
  if (props.disableModal) {
    return (
      <div id="social-connect-menu" className={styles.outerwrap}>
        <div
          className={styles["social-connect-container"]}
          style={props.socialContainerStyles}
        >
          <div
            className={styles["links-container"]}
            style={props.linksContainerStyles}
          >
            {outputArray}
          </div>
        </div>
      </div>
    );
  }

  /*
    Portal the modal to document.body. App-header uses backdrop-filter, which
    makes position:fixed descendants resolve against the header box (navbar
    height) instead of the viewport; so the sheet sat at the top and clipped.
  */
  const modal =
    typeof document !== "undefined"
      ? createPortal(
          <div
            id="ms1-follow-modal"
            className={
              styles["follow-modal"] +
              (modalOpen ? " " + styles["follow-modal-open"] : "")
            }
            aria-hidden={!modalOpen}
            onClick={closeModal}
          >
            <div
              ref={sheetRef}
              className={styles["follow-sheet"]}
              role="dialog"
              aria-modal="true"
              aria-label="Follow on social media"
              data-scroll-lock-scrollable
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles["follow-sheet-glow"]} aria-hidden="true" />

              <header className={styles["follow-sheet-header"]}>
                <h2 className={styles["follow-sheet-title"]}>Connect</h2>
                <button
                  ref={closeRef}
                  type="button"
                  className={styles["follow-sheet-close"]}
                  onClick={closeModal}
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </header>

              <div className={styles["follow-sheet-links"]}>{outputArray}</div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div id="social-connect-menu" className={styles.outerwrap}>
      <button
        ref={triggerRef}
        type="button"
        className={styles["follow-trigger"]}
        onClick={toggleModal}
        aria-expanded={modalOpen}
        aria-controls="ms1-follow-modal"
        aria-haspopup="dialog"
      >
        Follow
      </button>
      {modal}
    </div>
  );
}

export default SocialConnectMenu;
